import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DocumentReviewState } from '../../../interfaces/document';
import { OpsReferralListParams } from '../../../interfaces/referral';
import { PROGRAM_COHORT_OPTIONS } from '../../../lib/referrals/cohorts';
import { parseReviewStates } from '../../document/hooks/useDocumentFilters';

const KNOWN_COHORTS = new Set<string>(PROGRAM_COHORT_OPTIONS.map(({ value }) => value));

/**
 * The data-quality states a referral can be filtered by. Not `failed`: leads-api derives it only
 * for a document that produced no patient document, which never has a referral, so
 * `GET /v1/ops/referrals` refuses it (400). Failed intake is worked in the Documents section.
 */
export const REFERRAL_HUB_REVIEW_STATES: readonly DocumentReviewState[] = [
  DocumentReviewState.Parsed,
  DocumentReviewState.NeedsReview,
  DocumentReviewState.Processing,
];

/**
 * The Referrals list's "Needs attention" view. Narrower than the Documents list's
 * `REVIEW_STATES_NEEDING_ATTENTION`, which adds `failed`: see above — failed intake has no
 * referral, and is worked in the Documents section.
 */
export const REFERRAL_HUB_NEEDING_ATTENTION: readonly DocumentReviewState[] = [
  DocumentReviewState.NeedsReview,
];

export const DEFAULT_REFERRAL_HUB_LIMIT = 20;
const MAX_PAGE = 1000;
export const MAX_SEARCH_LENGTH = 200;
const MAX_INT4 = 2147483647;

export type ReferralHubFilters = Required<
  Pick<
    OpsReferralListParams,
    | 'page'
    | 'limit'
    | 'search'
    | 'clinicIds'
    | 'reviewStates'
    | 'systemSources'
    | 'includeDiscarded'
  >
>;

const EMPTY: ReferralHubFilters = {
  page: 1,
  limit: DEFAULT_REFERRAL_HUB_LIMIT,
  search: '',
  clinicIds: [],
  reviewStates: [],
  systemSources: [],
  includeDiscarded: false,
};

const positiveInt = (value: string | null, fallback: number): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Filters from the URL.
 *
 * Validated rather than cast: a bookmark from an older build, or a hand-edited link, must not
 * be able to put a value into the query the API will reject — the list would then fail to
 * load instead of just showing fewer rows.
 */
export const parseReferralHubFilters = (search: string): ReferralHubFilters => {
  const params = new URLSearchParams(search);
  const list = (key: string) =>
    (params.get(key) ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

  return {
    // The API's bounds: a page up to 1000, a search up to 200 characters, an int4 clinic id.
    page: Math.min(positiveInt(params.get('page'), 1), MAX_PAGE),
    limit: Math.min(positiveInt(params.get('limit'), DEFAULT_REFERRAL_HUB_LIMIT), 100),
    search: (params.get('search') ?? '').slice(0, MAX_SEARCH_LENGTH),
    clinicIds: list('clinicIds')
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0 && id <= MAX_INT4),
    // A link from before the API stopped accepting `failed` must not make the list fail.
    reviewStates: (parseReviewStates(params.get('reviewStates')) ?? []).filter((state) =>
      REFERRAL_HUB_REVIEW_STATES.includes(state),
    ),
    systemSources: list('systemSources').filter((source) => KNOWN_COHORTS.has(source)),
    includeDiscarded: params.get('includeDiscarded') === 'true',
  };
};

/** The URL for these filters, leaving defaults out so a plain link stays plain. */
export const serializeReferralHubFilters = (filters: ReferralHubFilters): string => {
  const params = new URLSearchParams();
  if (filters.page !== 1) params.set('page', String(filters.page));
  if (filters.limit !== DEFAULT_REFERRAL_HUB_LIMIT) params.set('limit', String(filters.limit));
  if (filters.search.trim()) params.set('search', filters.search.trim());
  if (filters.clinicIds.length) params.set('clinicIds', filters.clinicIds.join(','));
  if (filters.reviewStates.length) params.set('reviewStates', filters.reviewStates.join(','));
  if (filters.systemSources.length) params.set('systemSources', filters.systemSources.join(','));
  if (filters.includeDiscarded) params.set('includeDiscarded', 'true');
  return params.toString();
};

/**
 * The Referrals list's filters, kept in the URL so a filtered view survives opening a
 * referral and coming back, a reload, or being pasted to a colleague.
 */
export const useReferralHubFilters = () => {
  const location = useLocation();
  const history = useHistory();
  const [filters, setFilters] = useState<ReferralHubFilters>(() =>
    parseReferralHubFilters(location.search),
  );
  // The list's own path. Once the operator has navigated away — opening a referral — the
  // location belongs to another page, and rewriting its query would erase that page's own
  // parameters (the document's `?tab=`).
  const listPath = useRef(location.pathname);
  // The query string the filters and the URL last agreed on. A URL that differs from it was
  // navigated to — the menu item clicked on a filtered view, an in-app `/referrals/hub?…` link —
  // and is read into the filters. The filters were read from the URL only once, so such a
  // navigation used to be replaced straight back with the old filters.
  const inSync = useRef(location.search.replace(/^\?/, ''));

  useEffect(() => {
    if (location.pathname !== listPath.current) {
      return;
    }
    const current = location.search.replace(/^\?/, '');
    if (current !== inSync.current) {
      inSync.current = current;
      setFilters(parseReferralHubFilters(location.search));
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname !== listPath.current) {
      return;
    }
    const next = serializeReferralHubFilters(filters);
    if (next !== inSync.current) {
      inSync.current = next;
      history.replace({ pathname: location.pathname, search: next ? `?${next}` : '' });
    }
    // Only a change of the filters writes the URL; a change of the URL is read above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  /** Any filter change goes back to page 1: page 4 of the old result means nothing. */
  const updateFilter = useCallback(
    <K extends keyof ReferralHubFilters>(key: K, value: ReferralHubFilters[K]) => {
      setFilters((current) => ({
        ...current,
        [key]: value,
        page: key === 'page' ? (value as number) : 1,
      }));
    },
    [],
  );

  const reset = useCallback(() => setFilters(EMPTY), []);

  const showNeedingAttention = useCallback(
    (states: DocumentReviewState[]) =>
      setFilters((current) => ({ ...current, reviewStates: states, page: 1 })),
    [],
  );

  return { filters, updateFilter, reset, showNeedingAttention };
};
